import { useQuery } from '@apollo/client';

import {
  ProductType,
  PurchaseWebCampaignType,
} from '@xing-com/crate-common-graphql-types';
import { redirectTo, useLocation } from '@xing-com/crate-core-router';
import { GetCampaignDocument } from '@xing-com/crate-upsell-graphql';
import type { GetCampaignQuery } from '@xing-com/crate-upsell-graphql';
import {
  isPayOrConsentUpsellMid,
  useProductParameter,
} from '@xing-com/crate-upsell-utils';
import { useExperiment } from '@xing-com/hub';

import { useLanguage } from './use-language';

/**
 * Get prices to show contract options user can buy.
 * if user already member then gets redirect to features page
 *
 * @returns {object} { loading, status, product, campaign }
 */
export function useCampaign(): {
  loading: boolean;
  status:
    | 'SUCCESS'
    | 'NO_PRODUCT_PARAMETER'
    | 'NO_CAMPAIGN_DATA'
    | 'LOADING'
    | 'NETWORK_ERROR';
  product: ProductType;
  campaign: GetCampaignQuery['results'] | null;
} {
  const { product } = useProductParameter();
  const { search } = useLocation();
  const locationSearch =
    search ?? (typeof window === 'undefined' ? '' : window.location.search);
  const originalMid =
    new URLSearchParams(locationSearch).get('mid') ?? undefined;
  const { loadingLanguage, language } = useLanguage();
  const payOrConsentExperimentEnabled = useExperiment('ABACUS-546') === 'B';
  const payOrConsentOfferButNotEligible =
    isPayOrConsentUpsellMid(originalMid) && !payOrConsentExperimentEnabled;

  const mid = payOrConsentOfferButNotEligible ? undefined : originalMid;

  const {
    loading: loadingCampaign,
    error: campaignError,
    data,
  } = useQuery(GetCampaignDocument, {
    skip: !product || loadingLanguage,
    variables: { mid, product, language },
  });

  if (loadingCampaign || loadingLanguage)
    return { loading: true, status: 'LOADING', product, campaign: null };

  if (campaignError) {
    return {
      loading: false,
      status: 'NETWORK_ERROR',
      product,
      campaign: null,
    };
  }

  // user is already member
  if (!data || !data.results) {
    redirectTo(`/${product}/features`);
    return {
      loading: false,
      status: 'NO_CAMPAIGN_DATA',
      product,
      campaign: null,
    };
  }

  if (data?.results?.campaignType === PurchaseWebCampaignType.Revoke) {
    return {
      loading: false,
      status: 'SUCCESS',
      product: product ?? ProductType.Premium,
      campaign: data.results,
    };
  }

  return {
    loading: false,
    status: 'SUCCESS',
    product: product ?? ProductType.Premium,
    campaign: data.results,
  };
}
